import { useEffect, useState } from "react";
import { useLocation, Link } from "wouter";
import Layout from "@/components/Layout";
import { useSEO } from "@/hooks/useSEO";
import { HIZMETLER, type HizmetVerisi } from "@/lib/hizmet_data";
import { ChevronDown, Zap, TrendingUp, MessageSquareQuote } from "lucide-react";
import NotFound from "@/pages/NotFound";
import { trpc } from "@/lib/trpc";

/** Hizmet sayfalarının altındaki başvuru formu — talepler admin@dijipo.com'a e-posta olarak düşer. */
function BasvuruFormu({ hizmetAdi }: { hizmetAdi: string }) {
  const [ad, setAd] = useState("");
  const [eposta, setEposta] = useState("");
  const [telefon, setTelefon] = useState("");
  const [mesaj, setMesaj] = useState("");
  const [gonderildi, setGonderildi] = useState(false);
  const submit = trpc.form.submit.useMutation();

  const gonder = (e: React.FormEvent) => {
    e.preventDefault();
    if (!ad || (!eposta && !telefon)) return;
    submit.mutate(
      {
        formType: "contact",
        name: ad,
        email: eposta || undefined,
        phone: telefon || undefined,
        message: mesaj || undefined,
        metadata: JSON.stringify({ kaynak: "adsmanager.com.tr", hizmet: hizmetAdi }),
      },
      { onSuccess: () => setGonderildi(true), onError: () => setGonderildi(true) }
    );
  };

  if (gonderildi) {
    return (
      <div className="bg-white border-3 border-[#0A1A3F] p-8 text-center shadow-[4px_4px_0_0_#6B8AFE]">
        <p className="font-black text-xl text-[#0A1A3F]">Talebiniz alındı! 🎉</p>
        <p className="text-sm text-[#0A1A3F]/70 font-medium mt-2">Ekibimiz en kısa sürede sizinle iletişime geçecek.</p>
      </div>
    );
  }

  const inputCls =
    "w-full border-3 border-[#0A1A3F] bg-white px-4 py-3 text-base sm:text-sm font-medium text-[#0A1A3F] placeholder:text-[#0A1A3F]/50 focus:outline-none focus:shadow-[2px_2px_0_0_#6B8AFE]";
  return (
    <form onSubmit={gonder} className="bg-white border-3 border-[#0A1A3F] p-5 sm:p-6 lg:p-8 shadow-[4px_4px_0_0_#6B8AFE] text-left space-y-4">
      <h3 className="font-black text-xl text-[#0A1A3F]">Hızlı Başvuru</h3>
      <p className="text-xs text-[#0A1A3F]/60 font-medium">Bilgilerinizi bırakın, aynı gün dönüş yapalım.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input className={inputCls} placeholder="Adınız Soyadınız *" aria-label="Adınız Soyadınız" autoComplete="name" value={ad} onChange={e => setAd(e.target.value)} required />
        <input className={inputCls} type="tel" placeholder="Telefon" aria-label="Telefon" autoComplete="tel" inputMode="tel" value={telefon} onChange={e => setTelefon(e.target.value)} />
      </div>
      <input className={inputCls} type="email" placeholder="E-posta" aria-label="E-posta" autoComplete="email" inputMode="email" value={eposta} onChange={e => setEposta(e.target.value)} />
      <textarea className={inputCls} rows={3} placeholder="Kısaca ihtiyacınız (isteğe bağlı)" aria-label="Kısaca ihtiyacınız" value={mesaj} onChange={e => setMesaj(e.target.value)} />
      <button
        type="submit"
        disabled={submit.isPending}
        className="w-full bg-[#0A1A3F] text-white border-3 border-[#0A1A3F] px-6 sm:px-8 py-3.5 font-bold text-sm shadow-[3px_3px_0_0_#6B8AFE] hover:translate-x-[1px] hover:translate-y-[1px] transition-all disabled:opacity-60"
      >
        {submit.isPending ? "Gönderiliyor..." : "Başvuruyu Gönder"}
      </button>
    </form>
  );
}

/**
 * Veri tabanlı hizmet sayfası şablonu (AdsManager tasarım dili).
 * İçerik hizmet başına özgün yazılır (hizmet_data.ts); şablon tasarımı,
 * meta etiketlerini ve JSON-LD şema üretimini standartlaştırır.
 */
function HizmetIcerik({ veri }: { veri: HizmetVerisi }) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const url = `https://adsmanager.com.tr${veri.slug}`;

  useSEO({
    title: veri.seoTitle,
    description: veri.seoDescription,
    canonical: url,
  });

  // JSON-LD şemalarını sayfaya enjekte et
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          serviceType: veri.serviceType,
          name: `${veri.breadcrumbName} — AdsManager`,
          provider: { "@id": "https://adsmanager.com.tr/#organization" },
          description: veri.seoDescription,
          areaServed: { "@type": "Country", name: "Türkiye" },
          url,
        },
        {
          "@type": "FAQPage",
          mainEntity: veri.faqs.map(f => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: "https://adsmanager.com.tr/" },
            { "@type": "ListItem", position: 2, name: veri.breadcrumbName, item: url },
          ],
        },
      ],
    };
    const el = document.createElement("script");
    el.id = "hizmet-json-ld";
    el.type = "application/ld+json";
    el.textContent = JSON.stringify(schema);
    document.getElementById("hizmet-json-ld")?.remove();
    document.head.appendChild(el);
    return () => document.getElementById("hizmet-json-ld")?.remove();
  }, [veri]);

  return (
    <Layout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#FFFDF7] border-b-3 border-[#0A1A3F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 border-3 border-[#0A1A3F] bg-[#6B8AFE]/10 px-3 sm:px-4 py-2 mb-6 shadow-[2px_2px_0_0_#0A1A3F] max-w-full">
              <Zap className="w-4 h-4 text-[#6B8AFE] flex-shrink-0" />
              <span className="text-xs font-bold text-[#0A1A3F] uppercase tracking-wider">{veri.badge}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#0A1A3F] leading-[1.08] sm:leading-[1.05] tracking-tight mb-6 break-words">
              {veri.h1Top}{" "}
              <span className="relative inline-block text-[#6B8AFE]">
                {veri.h1Vurgu}
                <svg className="absolute left-0 -bottom-2 w-full h-3 fill-none" viewBox="0 0 100 10" preserveAspectRatio="none">
                  <path d="M0,5 Q25,10 50,5 T100,5" stroke="#0A1A3F" strokeWidth="2.5" fill="none" />
                </svg>
              </span>
            </h1>
            <p className="text-base lg:text-lg text-[#0A1A3F]/70 font-medium leading-relaxed mb-8">{veri.heroText}</p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
              <a
                href="#basvuru"
                className="bg-[#0A1A3F] text-white border-3 border-[#0A1A3F] px-6 py-3.5 sm:py-3 font-bold text-sm text-center shadow-[3px_3px_0_0_#6B8AFE] hover:translate-x-[1px] hover:translate-y-[1px] transition-all"
              >
                Hemen Başvur
              </a>
              <a
                href="tel:+905320522545"
                className="bg-white text-[#0A1A3F] border-3 border-[#0A1A3F] px-6 py-3.5 sm:py-3 font-bold text-sm text-center shadow-[3px_3px_0_0_#0A1A3F] hover:translate-x-[1px] hover:translate-y-[1px] transition-all"
              >
                Bizi Arayın
              </a>
            </div>
          </div>
          <div className="min-w-0">
            <div className="bg-white border-3 border-[#0A1A3F] p-5 sm:p-6 shadow-[6px_6px_0_0_#0A1A3F] space-y-4">
              <h3 className="font-black text-xl text-[#0A1A3F]">{veri.panelTitle}</h3>
              <p className="text-sm text-[#0A1A3F]/60 font-medium leading-relaxed">{veri.panelText}</p>
              <div className="border-t-2 border-[#0A1A3F] pt-4 grid grid-cols-1 min-[400px]:grid-cols-2 gap-2 sm:gap-3 text-xs font-mono font-bold text-[#0A1A3F]/70">
                {veri.panelTags.map(t => (
                  <span key={t} className="break-words">✓ {t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GEO: Kısa Cevap — yapay zeka asistanlarının alıntıladığı doğrudan cevap bloğu */}
      {veri.kisaCevap && (
        <section className="bg-white border-b-3 border-[#0A1A3F]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-12">
            <div className="border-3 border-[#0A1A3F] bg-[#6B8AFE]/10 p-6 lg:p-8 shadow-[4px_4px_0_0_#0A1A3F] space-y-3">
              <div className="flex items-center gap-2">
                <MessageSquareQuote className="w-5 h-5 text-[#6B8AFE]" />
                <span className="text-xs font-bold text-[#0A1A3F] uppercase tracking-wider">Kısa Cevap</span>
              </div>
              <h2 className="text-xl lg:text-2xl font-black text-[#0A1A3F] tracking-tight">{veri.kisaCevapSoru}</h2>
              <p className="text-sm lg:text-base text-[#0A1A3F]/80 font-medium leading-relaxed">{veri.kisaCevap}</p>
            </div>
          </div>
        </section>
      )}

      {/* Detay kartları */}
      <section className="bg-white border-b-3 border-[#0A1A3F]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 space-y-10 sm:space-y-12">
          <div className="max-w-3xl">
            <h2 className="text-[1.75rem] sm:text-3xl lg:text-4xl font-black text-[#0A1A3F] tracking-tight mb-4 break-words">{veri.sectionTitle}</h2>
            <p className="text-base text-[#0A1A3F]/70 font-medium leading-relaxed">{veri.sectionIntro}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8">
            {veri.cards.map((card, i) => (
              <div key={i} className="bg-[#FFFDF7] border-3 border-[#0A1A3F] p-5 sm:p-6 shadow-[4px_4px_0_0_#0A1A3F] space-y-3 min-w-0">
                <div className="bg-[#6B8AFE] text-white border-2 border-[#0A1A3F] w-9 h-9 flex items-center justify-center font-black text-sm shadow-[2px_2px_0_0_#0A1A3F]">
                  {i + 1}
                </div>
                <h3 className="font-black text-lg text-[#0A1A3F]">{card.t}</h3>
                <p className="text-sm text-[#0A1A3F]/60 font-medium leading-relaxed">{card.d}</p>
              </div>
            ))}
          </div>

          <div className="bg-[#0A1A3F] text-white border-3 border-[#0A1A3F] p-5 sm:p-8 shadow-[6px_6px_0_0_#6B8AFE] space-y-5">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-6 h-6 text-[#6B8AFE] flex-shrink-0" />
              <h3 className="font-black text-xl text-[#6B8AFE]">{veri.darkTitle}</h3>
            </div>
            <p className="text-sm text-white/70 font-medium leading-relaxed">{veri.darkText}</p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
              <a
                href="https://wa.me/905320522545?text=Merhaba%2C%20reklam%20hesab%C4%B1m%20i%C3%A7in%20%C3%BCcretsiz%20g%C3%B6r%C3%BC%C5%9Fme%20talep%20ediyorum."
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#6B8AFE] text-white border-2 border-white px-5 py-3 sm:py-2.5 font-bold text-sm text-center shadow-[2px_2px_0_0_#fff] hover:translate-x-[1px] hover:translate-y-[1px] transition-all"
              >
                Ücretsiz Görüşme
              </a>
              <Link
                href="/fiyatlar"
                className="bg-white text-[#0A1A3F] border-2 border-white px-5 py-3 sm:py-2.5 font-bold text-sm text-center shadow-[2px_2px_0_0_#6B8AFE] hover:translate-x-[1px] hover:translate-y-[1px] transition-all"
              >
                Fiyatları İncele
              </Link>
            </div>
          </div>

          {/* SSS */}
          <div className="border-t-3 border-[#0A1A3F] pt-10 sm:pt-12 space-y-6">
            <h2 className="text-[1.75rem] sm:text-3xl font-black text-[#0A1A3F] tracking-tight">Sıkça Sorulan Sorular</h2>
            <div className="space-y-4">
              {veri.faqs.map((faq, index) => (
                <div key={index} className="border-3 border-[#0A1A3F] bg-white shadow-[4px_4px_0_0_#0A1A3F]">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex justify-between items-center gap-3 p-4 sm:p-5 text-left font-black text-sm md:text-base text-[#0A1A3F] hover:bg-[#FFFDF7] transition-colors"
                    aria-expanded={openFaq === index}
                  >
                    <span>{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${openFaq === index ? "rotate-180" : ""}`} />
                  </button>
                  {openFaq === index && (
                    <div className="p-4 sm:p-5 border-t-3 border-[#0A1A3F] bg-[#FFFDF7] text-sm text-[#0A1A3F]/70 font-medium leading-relaxed">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* İlgili sayfalar */}
          {veri.ilgili && veri.ilgili.length > 0 && (
            <div className="border-t-3 border-[#0A1A3F] pt-10 space-y-4">
              <h2 className="text-lg font-black text-[#0A1A3F] tracking-tight">İlgili Hizmetler</h2>
              <div className="flex flex-wrap gap-3">
                {veri.ilgili.map(l =>
                  l.href.startsWith("http") ? (
                    <a key={l.href} href={l.href} className="border-2 border-[#0A1A3F] bg-white px-4 py-2 text-xs font-bold text-[#0A1A3F] hover:bg-[#6B8AFE]/15 transition-colors">
                      {l.label} ↗
                    </a>
                  ) : (
                    <Link key={l.href} href={l.href} className="border-2 border-[#0A1A3F] bg-white px-4 py-2 text-xs font-bold text-[#0A1A3F] hover:bg-[#6B8AFE]/15 transition-colors">
                      {l.label}
                    </Link>
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section id="basvuru" className="bg-[#FFFDF7] scroll-mt-20 sm:scroll-mt-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-center space-y-6">
          <h2 className="text-[1.75rem] sm:text-3xl lg:text-4xl font-black text-[#0A1A3F] break-words">{veri.ctaTitle}</h2>
          <p className="text-sm text-[#0A1A3F]/70 font-medium leading-relaxed">{veri.ctaText}</p>
          <BasvuruFormu hizmetAdi={veri.breadcrumbName} />
          <p className="text-xs text-[#0A1A3F]/60 font-medium break-words">
            Veya hemen arayın:{" "}
            <a href="tel:+905320522545" className="underline font-bold inline-block py-2 text-[#0A1A3F]">0532 052 25 45</a>
          </p>
        </div>
      </section>
    </Layout>
  );
}

export default function HizmetSayfasi() {
  const [location] = useLocation();
  const veri = HIZMETLER.find(h => h.slug === location);
  if (!veri) return <NotFound />;
  return <HizmetIcerik veri={veri} />;
}
